import { useState, useCallback, useEffect, useMemo, memo, useRef } from "react";
import {
  Box,
  TextField,
  Typography,
  Stack,
  Paper,
  Tabs,
  Tab,
  Button,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Alert,
  Checkbox,
  InputAdornment,
  CircularProgress,
  IconButton,
  Popper,
  Fade,
  ClickAwayListener,
  FormControlLabel,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useDropzone } from "react-dropzone";
import * as XLSX from "xlsx";
import ContactsIcon from "@mui/icons-material/Contacts";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import PeopleIcon from "@mui/icons-material/People";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import FilterListIcon from "@mui/icons-material/FilterList";
import { getLeadsApi } from "../../../api/leads.api";
import { useSettings } from "../../../context/SettingsContext";

export default function ContactUploadSection({
  contacts,
  contactStats,
  fieldMapping,
  csvHeaders,
  errors,
  onContactsParsed,
  onFieldMappingChange,
  onCsvHeaders,
}) {
  const theme = useTheme();
  const settings = useSettings();
  const [tab, setTab] = useState(0);
  const [rawData, setRawData] = useState([]);
  const [pasteText, setPasteText] = useState("");
  const [fileName, setFileName] = useState("");
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsSearch, setLeadsSearch] = useState("");
  const [selectedLeadIds, setSelectedLeadIds] = useState(new Set());

  // Filter state for Existing tab
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [selectedSources, setSelectedSources] = useState([]);
  const [selectedTreatments, setSelectedTreatments] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState([]);

  const sourceOptions = useMemo(() => {
    const list = settings?.lead_features?.sources || [];
    return list
      .map((s) => (typeof s === "string" ? s : s?.key || s?.name))
      .filter(Boolean);
  }, [settings]);

  const treatmentOptions = useMemo(() => {
    return (settings?.services || []).filter(Boolean);
  }, [settings]);

  const prettifySource = (s) =>
    String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const responseLabels = [
    { key: "hot", label: "Hot" },
    { key: "cold", label: "Cold" },
    { key: "warm", label: "Warm" },
    { key: "new", label: "New" },
    { key: "dropped", label: "Dropped" },
    { key: "qualified", label: "Qualified Leads" },
    { key: "need_analysis", label: "Need Analysis" },
    { key: "booked", label: "Booked" },
    { key: "post_consult", label: "Post-Consult" },
    { key: "proposal_shared", label: "Proposal-Shared" },
    { key: "negotiation", label: "Negotiation" },
    { key: "treatment_booked", label: "Treatment-Booked" },
    { key: "won", label: "Won" },
  ];

  // CSV/Excel upload
  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      const wb = XLSX.read(e.target.result, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { header: 1 });

      if (json.length === 0) return;

      let headers = [];
      let rows = [];

      // Heuristic: Check if first row is header or data
      const firstRow = json[0];
      const firstCell = String(firstRow[0]).trim();
      
      // Simple check: if first cell looks like a phone number (digits, +, spaces, dashes), treat as data
      const isPhoneNumber = /^[\d+\-\s()]+$/.test(firstCell) && firstCell.replace(/\D/g, '').length > 5;
      
      if (isPhoneNumber && json.length > 0) {
         // Assume headerless
         headers = firstRow.map((_, i) => `Column ${i + 1}`);
         rows = json.map(row => {
            const obj = {};
            headers.forEach((h, i) => {
                obj[h] = row[i] != null ? String(row[i]).trim() : "";
            });
            return obj;
         });
      } else if (json.length > 1) {
         // Assume standard header + data
         headers = firstRow.map((h) => String(h).trim());
         rows = json.slice(1).map((row) => {
            const obj = {};
            headers.forEach((h, i) => {
               obj[h] = row[i] != null ? String(row[i]).trim() : "";
            });
            return obj;
         });
      } else {
         // Only 1 row and doesn't look like phone number? Treat as header only (no data) or data only?
         // If it's just 1 row and not phone number, it's ambiguous. 
         // But if we are strict, we might fail. Let's assume it's data if it has content, or header if it looks like words.
         // For now, allow 1 row as data if it has content
         if (firstRow.length > 0) {
             headers = firstRow.map((_, i) => `Column ${i + 1}`);
             rows = json.map(row => {
                const obj = {};
                headers.forEach((h, i) => {
                    obj[h] = row[i] != null ? String(row[i]).trim() : "";
                });
                return obj;
             });
         } else {
            return;
         }
      }

      onCsvHeaders(headers);
      setRawData(rows);
      autoDetectMapping(headers, rows);
    };
    reader.readAsArrayBuffer(file);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "application/vnd.ms-excel": [".xls"],
    },
    multiple: false,
  });

  const autoDetectMapping = (headers, data = []) => {
    const mapping = { phone: "", name: "" };
    
    // 1. Try header names
    for (const h of headers) {
      const lower = h.toLowerCase();
      if (!mapping.phone && (lower.includes("phone") || lower.includes("mobile") || lower.includes("number") || lower.includes("contact"))) {
        mapping.phone = h;
      }
      if (!mapping.name && (lower.includes("name") || lower.includes("client"))) {
        mapping.name = h;
      }
    }

    // 2. If phone not found, try to look at data
    if (!mapping.phone && data.length > 0) {
      const firstRow = data[0];
      for (const h of headers) {
        const val = String(firstRow[h] || "").trim().replace(/\D/g, "");
        // If 10-15 digits, likely a phone number
        if (val.length >= 10 && val.length <= 15) {
           mapping.phone = h;
           break;
        }
      }
    }

    onFieldMappingChange(mapping);
  };

  // Apply field mapping to raw data
  useEffect(() => {
    if (rawData.length === 0 || !fieldMapping.phone) return;

    const mapped = rawData.map((row) => {
      const contact = {
        phone: row[fieldMapping.phone] || "",
        name: row[fieldMapping.name] || "",
        customFields: {},
      };
      Object.keys(row).forEach((k) => {
        if (k !== fieldMapping.phone && k !== fieldMapping.name) {
          contact.customFields[k] = row[k];
        }
      });
      return contact;
    });

    onContactsParsed(mapped);
  }, [rawData, fieldMapping]);

  // Manual paste
  const handlePasteProcess = () => {
    if (!pasteText.trim()) return;
    const lines = pasteText.trim().split("\n");
    if (lines.length === 0) return;

    const delimiter = lines[0].includes("\t") ? "\t" : ",";
    const headers = lines[0].split(delimiter).map((h) => h.trim());
    onCsvHeaders(headers);

    const rows = lines.slice(1).map((line) => {
      const cols = line.split(delimiter);
      const obj = {};
      headers.forEach((h, i) => {
        obj[h] = cols[i] ? cols[i].trim() : "";
      });
      return obj;
    });

    setRawData(rows);
    autoDetectMapping(headers, rows);
  };

  // Existing contacts
  const fetchLeads = async () => {
    setLeadsLoading(true);
    try {
      const res = await getLeadsApi({});
      const d = res.data?.data;
      setLeads(Array.isArray(d) ? d : d?.items || []);
    } catch {
      setLeads([]);
    } finally {
      setLeadsLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 2 && leads.length === 0) fetchLeads();
  }, [tab]);

  const toggleLead = useCallback((id) => {
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const applySelectedLeads = () => {
    const mapped = leads
      .filter((l) => selectedLeadIds.has(l._id))
      .map((l) => ({
        phone: l.contact || "",
        name: l.client_name || l.fullName || "",
        customFields: { email: l.email || "" },
      }));
    onCsvHeaders(["phone", "name", "email"]);
    onContactsParsed(mapped);
  };

  const filteredLeads = useMemo(() => {
    return leads.filter((l) => {
      // Search filter
      const matchesSearch =
        !leadsSearch ||
        (l.client_name || "").toLowerCase().includes(leadsSearch.toLowerCase()) ||
        (l.contact || "").includes(leadsSearch);
      if (!matchesSearch) return false;

      // Lead Source filter
      if (selectedSources.length > 0) {
        const src = l.custom_fields?.lead_source || l.source || "";
        if (!selectedSources.includes(src)) return false;
      }

      // Treatment Type filter
      if (selectedTreatments.length > 0) {
        const svc = l.services || [];
        if (!svc.some((s) => selectedTreatments.includes(s))) return false;
      }

      // Response Label filter
      if (selectedLabels.length > 0) {
        const key = l.current_status?.key || "";
        if (!selectedLabels.includes(key)) return false;
      }

      return true;
    });
  }, [leads, leadsSearch, selectedSources, selectedTreatments, selectedLabels]);

  const handleDownloadSampleCsv = () => {
    const csvContent = "phone,name\n+919876543210,John Doe\n+919876543211,Jane Doe\n+919876543212,Alex Smith";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "sample_contacts.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const tabLabels = [
    { label: "CSV", icon: <CloudUploadIcon sx={{ fontSize: 16 }} /> },
    { label: "Paste", icon: <ContentPasteIcon sx={{ fontSize: 16 }} /> },
    { label: "Existing", icon: <PeopleIcon sx={{ fontSize: 16 }} /> },
  ];

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2.5, md: 3 },
        mb: 3,
        borderRadius: 3,
        borderColor: errors.contacts ? theme.palette.error.main : "divider",
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ContactsIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.primary" }}>
            Contact Upload
          </Typography>
        </Stack>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
          onClick={handleDownloadSampleCsv}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: 11,
            color: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
            borderRadius: 2,
            px: 1.5,
            py: 0.25,
            "& .MuiButton-startIcon": { mr: 0.4 },
            "&:hover": { borderColor: theme.palette.primary.dark, bgcolor: `${theme.palette.primary.main}08` },
          }}
        >
          Sample CSV
        </Button>
      </Stack>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: 2,
          minHeight: 34,
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 600,
            fontSize: 12,
            minHeight: 34,
            py: 0.4,
            px: 1.5,
            minWidth: "auto",
          },
          "& .Mui-selected": { color: theme.palette.primary.main },
          "& .MuiTabs-indicator": { bgcolor: theme.palette.primary.main, height: 2 },
        }}
      >
        {tabLabels.map((t, i) => (
          <Tab key={i} label={t.label} icon={t.icon} iconPosition="start" />
        ))}
      </Tabs>

      {/* CSV Upload */}
      {tab === 0 && (
        <Stack spacing={2}>
          <Box
            {...getRootProps()}
            sx={{
              border: "2px dashed",
              borderColor: isDragActive ? theme.palette.primary.main : "#cbd5e1",
              borderRadius: 2.5,
              p: 3,
              textAlign: "center",
              cursor: "pointer",
              bgcolor: isDragActive ? `${theme.palette.primary.main}08` : "#f8fafc",
              transition: "all 0.15s",
              "&:hover": { borderColor: theme.palette.primary.main, bgcolor: `${theme.palette.primary.main}08` },
            }}
          >
            <input {...getInputProps()} />
            <CloudUploadIcon sx={{ fontSize: 36, color: "#94a3b8", mb: 0.75 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#475569" }}>
              {fileName || "Drop your file here or click to browse"}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.25 }}>
              Supports .csv, .xlsx, .xls
            </Typography>
          </Box>

          {csvHeaders.length > 0 && (
            <FieldMappingUI
              headers={csvHeaders}
              fieldMapping={fieldMapping}
              onFieldMappingChange={onFieldMappingChange}
            />
          )}
        </Stack>
      )}

      {/* Manual Paste */}
      {tab === 1 && (
        <Stack spacing={2}>
          <Typography sx={{ fontSize: 12, color: "#64748b" }}>
            Paste tab-delimited or comma-separated data. First row should be headers.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            size="small"
            placeholder={"phone,name\n+919876543210,John Doe\n+919876543211,Jane Doe"}
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            sx={inputSx}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handlePasteProcess}
            disabled={!pasteText.trim()}
            sx={{
              alignSelf: "flex-start",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            Process Data
          </Button>
          {csvHeaders.length > 0 && (
            <FieldMappingUI
              headers={csvHeaders}
              fieldMapping={fieldMapping}
              onFieldMappingChange={onFieldMappingChange}
            />
          )}
        </Stack>
      )}

      {/* Existing Contacts */}
      {tab === 2 && (
        <Stack spacing={2}>
          {leadsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by name or phone..."
                  value={leadsSearch}
                  onChange={(e) => setLeadsSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />
                <Box sx={{ position: "relative" }}>
                  <IconButton
                    size="small"
                    onClick={(e) => setFilterAnchor(filterAnchor ? null : e.currentTarget)}
                    sx={{
                      border: "1px solid",
                      borderColor: (selectedSources.length + selectedTreatments.length + selectedLabels.length) > 0
                        ? theme.palette.primary.main
                        : "#e2e8f0",
                      borderRadius: 2,
                      bgcolor: (selectedSources.length + selectedTreatments.length + selectedLabels.length) > 0
                        ? `${theme.palette.primary.main}10`
                        : "#fff",
                      p: 0.8,
                    }}
                  >
                    <FilterListIcon
                      sx={{
                        fontSize: 20,
                        color: (selectedSources.length + selectedTreatments.length + selectedLabels.length) > 0
                          ? theme.palette.primary.main
                          : "#64748b",
                      }}
                    />
                  </IconButton>
                  {(selectedSources.length + selectedTreatments.length + selectedLabels.length) > 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: -4,
                        right: -4,
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        bgcolor: theme.palette.primary.main,
                        color: "#fff",
                        fontSize: 9,
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {selectedSources.length + selectedTreatments.length + selectedLabels.length}
                    </Box>
                  )}
                </Box>
              </Stack>

              {/* Filter Dropdown */}
              <Popper
                open={Boolean(filterAnchor)}
                anchorEl={filterAnchor}
                placement="bottom-end"
                transition
                sx={{ zIndex: 1300 }}
              >
                {({ TransitionProps }) => (
                  <Fade {...TransitionProps} timeout={200}>
                    <Box>
                      <ClickAwayListener onClickAway={() => setFilterAnchor(null)}>
                        <Paper
                          elevation={8}
                          sx={{
                            width: 280,
                            borderRadius: 3,
                            overflow: "hidden",
                            border: "1px solid #e2e8f0",
                            mt: 0.5,
                          }}
                        >
                          <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #f1f5f9" }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>
                                Filters
                              </Typography>
                              <Button
                                size="small"
                                onClick={() => {
                                  setSelectedSources([]);
                                  setSelectedTreatments([]);
                                  setSelectedLabels([]);
                                }}
                                disabled={
                                  selectedSources.length === 0 &&
                                  selectedTreatments.length === 0 &&
                                  selectedLabels.length === 0
                                }
                                sx={{
                                  textTransform: "none",
                                  fontSize: 11,
                                  fontWeight: 600,
                                  minWidth: "auto",
                                  p: 0,
                                  color: theme.palette.primary.main,
                                }}
                              >
                                Clear all
                              </Button>
                            </Stack>
                          </Box>

                          <Box
                            sx={{
                              maxHeight: 320,
                              overflowY: "auto",
                              "&::-webkit-scrollbar": { width: 5 },
                              "&::-webkit-scrollbar-thumb": { bgcolor: "#cbd5e1", borderRadius: 3 },
                            }}
                          >
                            {/* Lead Source */}
                            {sourceOptions.length > 0 && (
                              <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #f8fafc" }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, mb: 0.8 }}>
                                  Lead Source
                                </Typography>
                                <Stack spacing={0}>
                                  {sourceOptions.map((src) => (
                                    <FormControlLabel
                                      key={src}
                                      control={
                                        <Checkbox
                                          size="small"
                                          checked={selectedSources.includes(src)}
                                          onChange={() => {
                                            setSelectedSources((prev) =>
                                              prev.includes(src)
                                                ? prev.filter((s) => s !== src)
                                                : [...prev, src]
                                            );
                                          }}
                                          sx={{ py: 0.3, "&.Mui-checked": { color: theme.palette.primary.main } }}
                                        />
                                      }
                                      label={
                                        <Typography sx={{ fontSize: 13, color: "#334155" }}>
                                          {prettifySource(src)}
                                        </Typography>
                                      }
                                    />
                                  ))}
                                </Stack>
                              </Box>
                            )}

                            {/* Treatment Type */}
                            {treatmentOptions.length > 0 && (
                              <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #f8fafc" }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, mb: 0.8 }}>
                                  Treatment Type
                                </Typography>
                                <Stack spacing={0}>
                                  {treatmentOptions.map((t) => (
                                    <FormControlLabel
                                      key={t}
                                      control={
                                        <Checkbox
                                          size="small"
                                          checked={selectedTreatments.includes(t)}
                                          onChange={() => {
                                            setSelectedTreatments((prev) =>
                                              prev.includes(t)
                                                ? prev.filter((x) => x !== t)
                                                : [...prev, t]
                                            );
                                          }}
                                          sx={{ py: 0.3, "&.Mui-checked": { color: theme.palette.primary.main } }}
                                        />
                                      }
                                      label={
                                        <Typography sx={{ fontSize: 13, color: "#334155" }}>
                                          {t}
                                        </Typography>
                                      }
                                    />
                                  ))}
                                </Stack>
                              </Box>
                            )}

                            {/* Response Label */}
                            {responseLabels.length > 0 && (
                              <Box sx={{ px: 2, py: 1.5 }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, mb: 0.8 }}>
                                  Response Label
                                </Typography>
                                <Stack spacing={0}>
                                  {responseLabels.map((item) => (
                                    <FormControlLabel
                                      key={item.key}
                                      control={
                                        <Checkbox
                                          size="small"
                                          checked={selectedLabels.includes(item.key)}
                                          onChange={() => {
                                            setSelectedLabels((prev) =>
                                              prev.includes(item.key)
                                                ? prev.filter((x) => x !== item.key)
                                                : [...prev, item.key]
                                            );
                                          }}
                                          sx={{ py: 0.3, "&.Mui-checked": { color: theme.palette.primary.main } }}
                                        />
                                      }
                                      label={
                                        <Typography sx={{ fontSize: 13, color: "#334155" }}>
                                          {item.label}
                                        </Typography>
                                      }
                                    />
                                  ))}
                                </Stack>
                              </Box>
                            )}
                          </Box>
                        </Paper>
                      </ClickAwayListener>
                    </Box>
                  </Fade>
                )}
              </Popper>

              {/* Active filter chips */}
              {(selectedSources.length + selectedTreatments.length + selectedLabels.length) > 0 && (
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {selectedSources.map((s) => (
                    <Chip
                      key={`src-${s}`}
                      label={prettifySource(s)}
                      size="small"
                      onDelete={() => setSelectedSources((p) => p.filter((x) => x !== s))}
                      sx={{ fontSize: 11, fontWeight: 600, bgcolor: `${theme.palette.primary.main}12`, color: theme.palette.primary.main }}
                    />
                  ))}
                  {selectedTreatments.map((t) => (
                    <Chip
                      key={`trt-${t}`}
                      label={t}
                      size="small"
                      onDelete={() => setSelectedTreatments((p) => p.filter((x) => x !== t))}
                      sx={{ fontSize: 11, fontWeight: 600, bgcolor: "#dcfce7", color: "#16a34a" }}
                    />
                  ))}
                  {selectedLabels.map((l) => (
                    <Chip
                      key={`lbl-${l}`}
                      label={responseLabels.find((r) => r.key === l)?.label || l}
                      size="small"
                      onDelete={() => setSelectedLabels((p) => p.filter((x) => x !== l))}
                      sx={{ fontSize: 11, fontWeight: 600, bgcolor: "#fef9c3", color: "#d97706" }}
                    />
                  ))}
                </Stack>
              )}

              {/* Select All / Deselect All + count */}
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={filteredLeads.length > 0 && filteredLeads.every((l) => selectedLeadIds.has(l._id))}
                      indeterminate={
                        filteredLeads.some((l) => selectedLeadIds.has(l._id)) &&
                        !filteredLeads.every((l) => selectedLeadIds.has(l._id))
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedLeadIds((prev) => {
                            const next = new Set(prev);
                            filteredLeads.forEach((l) => next.add(l._id));
                            return next;
                          });
                        } else {
                          setSelectedLeadIds((prev) => {
                            const next = new Set(prev);
                            filteredLeads.forEach((l) => next.delete(l._id));
                            return next;
                          });
                        }
                      }}
                      sx={{ "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: theme.palette.primary.main } }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>
                      Select All ({filteredLeads.length})
                    </Typography>
                  }
                />
                {selectedLeadIds.size > 0 && (
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: theme.palette.primary.main }}>
                    {selectedLeadIds.size} selected
                  </Typography>
                )}
              </Stack>

              <Box
                sx={{
                  maxHeight: 240,
                  overflowY: "auto",
                  border: "1px solid #e2e8f0",
                  borderRadius: 2,
                  "&::-webkit-scrollbar": { width: 6 },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: theme.palette.primary.main,
                    borderRadius: 3,
                  },
                }}
              >
                {filteredLeads.map((l) => (
                  <LeadRow
                    key={l._id}
                    lead={l}
                    isSelected={selectedLeadIds.has(l._id)}
                    onToggle={toggleLead}
                    primaryColor={theme.palette.primary.main}
                  />
                ))}
                {filteredLeads.length === 0 && (
                  <Typography sx={{ p: 2, textAlign: "center", fontSize: 13, color: "#94a3b8" }}>
                    No contacts found
                  </Typography>
                )}
              </Box>
              <Button
                variant="contained"
                size="small"
                onClick={applySelectedLeads}
                disabled={selectedLeadIds.size === 0}
                sx={{
                  alignSelf: "flex-start",
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                Add {selectedLeadIds.size} Contact{selectedLeadIds.size !== 1 ? "s" : ""}
              </Button>
            </>
          )}
        </Stack>
      )}

      {/* Validation Stats */}
      {contacts.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={1.5}>
            <StatChip
              icon={<ContactsIcon sx={{ fontSize: 16 }} />}
              label="Total"
              value={contactStats.total}
              color="#475569"
              bg="#f1f5f9"
            />
            <StatChip
              icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
              label="Valid"
              value={contactStats.valid}
              color="#16a34a"
              bg="#dcfce7"
            />
            <StatChip
              icon={<ErrorIcon sx={{ fontSize: 16 }} />}
              label="Invalid"
              value={contactStats.invalid}
              color="#dc2626"
              bg="#fef2f2"
            />
            <StatChip
              icon={<WarningIcon sx={{ fontSize: 16 }} />}
              label="Duplicates"
              value={contactStats.duplicates}
              color="#d97706"
              bg="#fef9c3"
            />
          </Stack>
        </Box>
      )}

      {errors.contacts && (
        <Alert severity="error" sx={{ mt: 1.5, fontSize: 12, borderRadius: 2 }}>
          {errors.contacts}
        </Alert>
      )}
    </Paper>
  );
}

const LeadRow = memo(function LeadRow({ lead, isSelected, onToggle, primaryColor }) {
  return (
    <Box
      onClick={() => onToggle(lead._id)}
      sx={{
        display: "flex",
        alignItems: "center",
        px: 1.5,
        py: 0.8,
        cursor: "pointer",
        borderBottom: "1px solid #f1f5f9",
        "&:hover": { bgcolor: "#f8fafc" },
      }}
    >
      <Checkbox
        size="small"
        checked={isSelected}
        sx={{ "&.Mui-checked": { color: primaryColor } }}
      />
      <Box sx={{ ml: 0.5 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#1e293b" }}>
          {lead.client_name || lead.fullName || "—"}
        </Typography>
        <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
          {lead.contact || "No phone"}
        </Typography>
      </Box>
    </Box>
  );
});

function FieldMappingUI({ headers, fieldMapping, onFieldMappingChange }) {
  return (
    <Box sx={{ p: 2, borderRadius: 2, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#475569", mb: 1.5 }}>
        Map Columns
      </Typography>
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#334155", minWidth: 120 }}>
            Phone Number *
          </Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={fieldMapping.phone || ""}
              onChange={(e) =>
                onFieldMappingChange({ ...fieldMapping, phone: e.target.value })
              }
              displayEmpty
              renderValue={(v) =>
                v || <span style={{ color: "#94a3b8" }}>Select column</span>
              }
              sx={selectSx}
            >
              {headers.map((h) => (
                <MenuItem key={h} value={h}>
                  {h}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#334155", minWidth: 120 }}>
            Name
          </Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={fieldMapping.name || ""}
              onChange={(e) =>
                onFieldMappingChange({ ...fieldMapping, name: e.target.value })
              }
              displayEmpty
              renderValue={(v) =>
                v || <span style={{ color: "#94a3b8" }}>Select column</span>
              }
              sx={selectSx}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {headers.map((h) => (
                <MenuItem key={h} value={h}>
                  {h}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Stack>
    </Box>
  );
}

function StatChip({ icon, label, value, color, bg }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.5,
        py: 0.75,
        borderRadius: 2,
        bgcolor: bg,
        flex: 1,
      }}
    >
      <Box sx={{ color }}>{icon}</Box>
      <Box>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color, lineHeight: 1 }}>
          {value}
        </Typography>
        <Typography sx={{ fontSize: 10, color, opacity: 0.8 }}>{label}</Typography>
      </Box>
    </Box>
  );
}

const inputSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "#fff",
    borderRadius: 2,
    fontSize: 13,
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: "primary.main" },
  },
};

const selectSx = {
  bgcolor: "#f8fafc",
  borderRadius: 2,
  fontSize: 13,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main" },
};
